import classnames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { HTMLAttributes, useState } from 'react'

// Components
import Icon from '@/assets/icons'
import Image from '@/components/common/Image'
import Link from 'next/link'

// Libs
import { useFollowingMembers } from '@/hooks/livemallclub/useFollow'

// Constants
import { USER_ROLE } from '@/types/user-profile'

// Styles
import { useAppSelector } from '@/redux/hooks'
import styles from './style.module.scss'
import useFollowParams from '@/hooks/useFollowerType'

const cx = classnames.bind(styles)

export type UserInfo = {
  merchantId?: string
  memberId: string
  coverImageUrl: string
  merchantName: string
  location: string
  isFollowed: boolean
}

export const UserCard = ({ user }: { user: UserInfo }) => {
  const { t } = useTranslation()
  const { coverImageUrl, merchantName, location, isFollowed } = user
  const { memberData } = useAppSelector((state) => state.member)
  const isMyAccount = memberData.memberInfoId === user.memberId
  const isMerchant = !!user.merchantId

  const [isFollowing, setIsFollowing] = useState(isFollowed)
  const { handleAddFollowingMember, handleUnFollowMember } = useFollowingMembers()
  const userRole = isMerchant ? USER_ROLE.MERCHANT : USER_ROLE.KOL
  const followerType = useFollowParams()

  const handleFollow = async () => {
    const action = isFollowing ? handleUnFollowMember : handleAddFollowingMember
    setIsFollowing((prev) => !prev)
    const res = await action({
      followeeId: isMerchant ? user.merchantId ?? '' : user.memberId,
      followeeType: userRole,
      followerType: followerType.role,
    })
    if (!res.isSuccess) {
      setIsFollowing((prev) => !prev)
    }
  }

  return (
    <div className={cx('user-card')}>
      <Link href={`/profile/${user.memberId}`} className={cx('user-card__link-wrapper')}>
        <div className={cx('user-card__image')}>
          {user.coverImageUrl ? (
            <Image src={coverImageUrl} alt={merchantName} width={60} height={60} style={{ objectFit: 'cover' }} />
          ) : (
            <Icon.DefaultHeadShot />
          )}
        </div>
        <div className={cx('user-card__info')}>
          <p className={cx('user-card__info__name')}>{merchantName}</p>
          {user.location && (
            <div className={cx('user-card__info__location')}>
              <Icon.Location />
              {location}
            </div>
          )}
        </div>
      </Link>
      {!isMyAccount && (
        <button className={cx('user-card__follow-button', { isFollowing })} onClick={handleFollow}>
          {isFollowing ? t('Follow_Button_Text_Following') : t('Follow_Button_Text_Follow')}
        </button>
      )}
    </div>
  )
}

export const UserCardList = ({ users, ...props }: HTMLAttributes<HTMLDivElement> & { users: UserInfo[] }) => {
  return (
    <div className={cx('user-card-list')} {...props}>
      {users.map((user) => (
        <UserCard key={user.merchantId} user={user} />
      ))}
    </div>
  )
}
